import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tabs,
  Tab
} from '@mui/material';
import { Visibility, TrendingUp, CompareArrows } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/authService';
import TrendsAnalytics from './TrendsAnalytics';
import MeetingComparison from './MeetingComparison';

const AnalyticsDashboard = () => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMeetings();
  }, []);

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/analytics/meetings');
      setMeetings(response.data);
    } catch (err) {
      console.error('Error fetching meetings:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Meeting Analytics
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
          <Tab label="Meetings" />
          <Tab label="Trends" icon={<TrendingUp />} iconPosition="start" />
          <Tab label="Compare" icon={<CompareArrows />} iconPosition="start" />
        </Tabs>
      </Box>

      {currentTab === 0 && (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Meetings
              </Typography>
              <Typography variant="h3">
                {meetings.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg Engagement Score
              </Typography>
              <Typography variant="h3">
                {meetings.length > 0
                  ? Math.round(
                      meetings.reduce((sum, m) => sum + (m.analytics?.engagementScore || 0), 0) /
                        meetings.length
                    )
                  : 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg Quality Score
              </Typography>
              <Typography variant="h3">
                {meetings.length > 0
                  ? Math.round(
                      meetings.reduce((sum, m) => sum + (m.analytics?.qualityScore || 0), 0) /
                        meetings.length
                    )
                  : 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Meetings
          </Typography>
          {meetings.length === 0 ? (
            <Alert severity="info">
              No ended meetings with analytics data yet. Analytics are generated after meetings end.
            </Alert>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Participants</TableCell>
                    <TableCell>Engagement</TableCell>
                    <TableCell>Quality</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {meetings.map((meeting) => (
                    <TableRow key={meeting.id}>
                      <TableCell>{meeting.title}</TableCell>
                      <TableCell>{formatDate(meeting.actualEndTime)}</TableCell>
                      <TableCell>
                        {formatDuration(meeting.analytics?.totalDurationSeconds)}
                      </TableCell>
                      <TableCell>{meeting.analytics?.participantCount || 0}</TableCell>
                      <TableCell>
                        {meeting.analytics ? (
                          <Chip
                            label={Math.round(meeting.analytics.engagementScore)}
                            color={getScoreColor(meeting.analytics.engagementScore)}
                            size="small"
                          />
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      <TableCell>
                        {meeting.analytics ? (
                          <Chip
                            label={Math.round(meeting.analytics.qualityScore)}
                            color={getScoreColor(meeting.analytics.qualityScore)}
                            size="small"
                          />
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/analytics/${meeting.id}`)}
                          title="View Details"
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
        </>
      )}

      {currentTab === 1 && <TrendsAnalytics />}
      {currentTab === 2 && <MeetingComparison />}
    </Box>
  );
};

export default AnalyticsDashboard;
